import React, { useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { Icon } from '@iconify/react';
import { useToast } from './ui/ToastProvider';
interface Transaction {
  id: string;
  mtoRef: string;
  bankRef: string;
  mtoName: string;
  remitterName: string;
  beneficiaryName: string;
  beneficiaryIBAN: string;
  amountFCY: string;
  currencyFCY: string;
  amountPKR: string;
  purposeCode: string;
  purposeText: string;
  payoutType: string;
  corridor: string;
  createdAt: string;
  ePRCNo?: string;
  ePRCDate?: string;
}
interface BulkEPRCModalProps {
  open: boolean;
  onClose: () => void;
  transactions: Transaction[];
}
const BulkEPRCModal: React.FC<BulkEPRCModalProps> = ({
  open,
  onClose,
  transactions
}) => {
  const {
    showToast
  } = useToast();
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = () => {
    if (printRef.current) {
      const printWindow = window.open('', '', 'height=600,width=800');
      if (printWindow) {
        printWindow.document.write('<html><head><title>Bulk e-PRC Certificates</title>');
        printWindow.document.write('<style>{`');
        printWindow.document.write(`
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0;
          }
          .page {
            page-break-after: always;
            page-break-inside: avoid;
            padding: 20mm;
            min-height: 100vh;
          }
          .page:last-child {
            page-break-after: auto;
          }
          .certificate-container {
            border: 2px solid #000 !important;
            padding: 30px;
          }
          table { 
            border-collapse: collapse !important; 
            width: 100% !important; 
            margin-bottom: 15px !important;
            border: 1px solid #000 !important;
          }
          td, th { 
            border: 1px solid #000 !important; 
            padding: 8px !important; 
            font-size: 11px !important;
          }
          .header { 
            text-align: center; 
            margin-bottom: 20px; 
            padding-bottom: 15px;
            border-bottom: 2px solid #000 !important;
          }
          .header h1 { 
            font-size: 18px; 
            margin: 0 0 5px 0;
            font-weight: bold;
          }
          .header p { 
            font-size: 13px; 
            margin: 0;
          }
          .section-header {
            background-color: #f5f5f5 !important;
            font-weight: bold !important;
          }
          .note {
            font-size: 10px;
            font-style: italic;
            margin-top: 10px;
          }
          .cert-text {
            font-size: 11px;
            line-height: 1.6;
            margin-bottom: 15px;
          }
          @page {
            size: A4 portrait;
            margin: 0;
          }
          @media print {
            body {
              margin: 0;
            }
            .page {
              page-break-after: always;
              page-break-inside: avoid;
              padding: 20mm;
              min-height: 100vh;
            }
            .page:last-child {
              page-break-after: auto;
            }
            .certificate-container {
              border: 2px solid #000 !important;
            }
            table, td, th {
              border: 1px solid #000 !important;
            }
            .section-header {
              background-color: #f5f5f5 !important;
            }
          }
        `);
        printWindow.document.write('`}</style></head><body>');
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      }
    }
  };
  const handleDownload = () => {
    showToast('Use Print button and select "Save as PDF" in the print dialog', 'info', 'Download as PDF', 5000);
    handlePrint();
  };
  // Extract country from corridor
  const getRemitterCountry = (corridor: string) => {
    const parts = corridor.split('-');
    return parts[0] || 'N/A';
  };
  // Get country code
  const getCountryCode = (corridor: string) => {
    const country = getRemitterCountry(corridor);
    const codes: Record<string, string> = {
      USA: 'US',
      UAE: 'AE',
      UK: 'GB',
      SAU: 'SA'
    };
    return codes[country] || country;
  };
  return <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{
    sx: {
      borderRadius: 2,
      maxHeight: '90vh'
    }
  }} data-id="element-203">
      <DialogTitle sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      pb: 2
    }} data-id="element-204">
        <Box data-id="element-205">
          <Typography variant="h6" fontWeight={600} data-id="element-206">
            Bulk e-PRC Certificate
          </Typography>
          <Typography variant="body2" color="text.secondary" data-id="element-207">
            {transactions.length} transactions selected
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" data-id="element-208">
          <Icon icon="lucide:x" width={20} height={20} data-id="element-209" />
        </IconButton>
      </DialogTitle>
      <DialogContent data-id="element-210">
        <Box ref={printRef} data-id="element-211">
          {transactions.map((transaction, index) => <Box key={transaction.id} className="page" data-id="element-212">
              <Box className="certificate-container" sx={{
            p: 4,
            border: '2px solid #000',
            borderRadius: 0,
            mb: index < transactions.length - 1 ? 4 : 0
          }} data-id="element-213">
                {/* Header */}
                <Box className="header" sx={{
              textAlign: 'center',
              mb: 3,
              pb: 2,
              borderBottom: '2px solid #000'
            }} data-id="element-214">
                  <Typography component="h1" sx={{
                fontSize: '1.25rem',
                fontWeight: 700,
                mb: 1
              }} data-id="element-215">
                    Electronic-Proceeds Realization Certificate (e-PRC)
                  </Typography>
                  <Typography component="p" sx={{
                fontSize: '0.9rem'
              }} data-id="element-216">
                    Personal / Corporate Customer
                  </Typography>
                </Box>

                {/* e-PRC Number and Date */}
                <TableContainer sx={{
              mb: 3
            }} data-id="element-217">
                  <Table size="small" sx={{
                border: '1px solid #000'
              }} data-id="element-218">
                    <TableBody data-id="element-219">
                      <TableRow data-id="element-220">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      width: '30%',
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-221">
                          e-PRC No. :
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-222">
                          {transaction.ePRCNo}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-223">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-224">
                          Date of e-PRC :
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-225">
                          {new Date(transaction.ePRCDate || '').toLocaleDateString('en-GB')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Remitter and Beneficiary Details */}
                <TableContainer sx={{
              mb: 3
            }} data-id="element-226">
                  <Table size="small" sx={{
                border: '1px solid #000'
              }} data-id="element-227">
                    <TableBody data-id="element-228">
                      <TableRow data-id="element-229">
                        <TableCell colSpan={2} className="section-header" sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-230">
                          Remitter's Detail:
                        </TableCell>
                        <TableCell colSpan={2} className="section-header" sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-231">
                          Beneficiary's Detail:
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-232">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      width: '25%',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-233">
                          Name of Person/ Entity
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      width: '25%',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-234">
                          {transaction.remitterName}
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      width: '25%',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-235">
                          Name of Person/ Entity
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      width: '25%',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-236">
                          {transaction.beneficiaryName}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-237">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-238">
                          NICOP/ Passport No./ CNIC/ POC/ Entity Registration
                          No./ Any other Unique ID
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-239">
                          N/A
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-240">
                          NICOP/ CNIC/ Passport No./ NTN
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-241">
                          N/A
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-242">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-243">
                          IBAN / Bank Account No. (if applicable)
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-244">
                          N/A
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-245">
                          Name of the Bank
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-246">
                          {transaction.beneficiaryIBAN.substring(4, 8)} Bank
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-247">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-248">
                          Name of Remitting (originating) Financial Institution
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-249">
                          {transaction.mtoName}
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-250">
                          IBAN / Bank Account No.
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-251">
                          {transaction.beneficiaryIBAN}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-252">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-253">
                          Name of Country
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-254">
                          {getRemitterCountry(transaction.corridor)}
                        </TableCell>
                        <TableCell colSpan={2} sx={{
                      border: '1px solid #000'
                    }} data-id="element-255" />
                      </TableRow>
                      <TableRow data-id="element-256">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-257">
                          Country Code (as per ITRS)
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-258">
                          {getCountryCode(transaction.corridor)}
                        </TableCell>
                        <TableCell colSpan={2} sx={{
                      border: '1px solid #000'
                    }} data-id="element-259" />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Certification Text */}
                <Box className="cert-text" sx={{
              mb: 2,
              fontSize: '0.8rem',
              lineHeight: 1.6
            }} data-id="element-260">
                  This is to certify that we have received remittance by the
                  order of the above mentioned Remitter and have paid the
                  equivalent Pak Rupees to the Beneficiary, as per the detail
                  given as below:
                </Box>

                {/* Transaction Details */}
                <TableContainer sx={{
              mb: 3
            }} data-id="element-261">
                  <Table size="small" sx={{
                border: '1px solid #000'
              }} data-id="element-262">
                    <TableBody data-id="element-263">
                      <TableRow data-id="element-264">
                        <TableCell colSpan={2} className="section-header" sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-265">
                          Transaction Detail:
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-266">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      width: '50%',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-267">
                          Date of Realization of Proceeds
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-268">
                          {new Date(transaction.createdAt).toLocaleDateString('en-GB')}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-269">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-270">
                          Amount of Remittance (in FCY)*
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-271">
                          {transaction.currencyFCY} {transaction.amountFCY}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-272">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-273">
                          Rate of Conversion*
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-274">
                          {(parseFloat(transaction.amountPKR) / parseFloat(transaction.amountFCY)).toFixed(2)}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-275">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-276">
                          Amount of Remittance (Local)
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-277">
                          {parseFloat(transaction.amountPKR).toFixed(2)}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-278">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-279">
                          Purpose Code (as per ITRS)
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-280">
                          {transaction.purposeCode}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-281">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-282">
                          Purpose of Remittance
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-283">
                          {transaction.purposeText}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-284">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-285">
                          Transaction Reference No.
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-286">
                          {transaction.bankRef}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Footer Notes */}
                <Box sx={{
              mb: 2
            }} className="note" data-id="element-287">
                  <Box sx={{
                fontSize: '0.7rem',
                mb: 1,
                fontStyle: 'italic'
              }} data-id="element-288">
                    [ The above transaction has been will be reported to SBP/
                    SBP-BSC in Schedule 54 / 03 of ITRS for the period : Nov
                    2025 (if applicable)]
                  </Box>
                  <Box sx={{
                fontSize: '0.7rem',
                fontWeight: 600
              }} data-id="element-289">
                    Note: This is a system-generated document. No signature is
                    required.
                  </Box>
                </Box>

                {/* Additional Note */}
                <Box className="note" sx={{
              fontSize: '0.65rem',
              fontStyle: 'italic'
            }} data-id="element-290">
                  * N/A in case the payment is received through Vostro account
                  of the remitting institution, and the rate of conversion has
                  been applied by the remitting institution abroad.
                </Box>
              </Box>
            </Box>)}
        </Box>
      </DialogContent>
      <DialogActions sx={{
      p: 3,
      pt: 2
    }} data-id="element-291">
        <Button variant="outlined" onClick={onClose} data-id="element-292">
          Close
        </Button>
        <Button variant="outlined" startIcon={<Icon icon="lucide:download" width={18} data-id="element-294" />} onClick={handleDownload} data-id="element-293">
          Download PDF
        </Button>
        <Button variant="contained" startIcon={<Icon icon="lucide:printer" width={18} data-id="element-296" />} onClick={handlePrint} data-id="element-295">
          Print
        </Button>
      </DialogActions>
    </Dialog>;
};
export default BulkEPRCModal;